import { TranslateModule } from '@ngx-translate/core';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter, map } from 'rxjs';
import { AuthService } from '../../../core/services/auth';
import { findWorkspaceGroup, visibleWorkspaceTabs } from './workspace-navigation';

@Component({
  selector: 'app-workspace-nav',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, RouterLink, RouterLinkActive],
  template: `
    @if (workspace(); as currentWorkspace) {
      @if (tabs().length > 1) {
        <section
          class="mb-4 rounded-2xl border border-hairline bg-panel px-3 py-3 shadow-sm"
          [attr.aria-labelledby]="'workspace-title-' + currentWorkspace.id">
          <div class="mb-2 flex items-center gap-2 px-1">
            <i [class]="currentWorkspace.icon + ' text-sky-600'" aria-hidden="true"></i>
            <h2
              class="text-xs font-semibold text-ink-muted"
              [id]="'workspace-title-' + currentWorkspace.id">
              {{ currentWorkspace.title }}
            </h2>
          </div>

          <label class="flex flex-col gap-1 text-sm font-semibold text-ink md:hidden">
            {{ 'HUB.WORKSPACE_VIEW' | translate }}
            <select class="min-h-11 rounded-xl border border-hairline bg-panel text-ink px-3"
              [value]="selectedTab()" (change)="navigateToTab($event)">
              @for (item of tabs(); track item.label; let index = $index) {
                <option [value]="index" [selected]="selectedTab() === index">{{ item.label }}</option>
              }
            </select>
          </label>
          <nav
            class="hidden md:flex gap-1 overflow-x-auto"
            [attr.aria-label]="'Vues de l’espace ' + currentWorkspace.title">
            @for (item of tabs(); track item.label) {
              <a
                [routerLink]="item.route"
                [queryParams]="item.queryParams"
                routerLinkActive
                #routeState="routerLinkActive"
                [routerLinkActiveOptions]="exactRouteMatch"
                [attr.aria-current]="routeState.isActive ? 'page' : null"
                class="inline-flex min-h-11 shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition-colors"
                [class.bg-primary-base]="routeState.isActive"
                [class.text-on-primary]="routeState.isActive"
                [class.shadow-sm]="routeState.isActive"
                [class.text-ink-muted]="!routeState.isActive"
                [class.hover:bg-chrome]="!routeState.isActive">
                <i [class]="item.icon + ' text-sm'" aria-hidden="true"></i>
                <span>{{ item.label }}</span>
              </a>
            }
          </nav>
        </section>
      }
    }
  `,
})
export class WorkspaceNavComponent {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  readonly exactRouteMatch = {
    paths: 'exact',
    queryParams: 'exact',
    matrixParams: 'ignored',
    fragment: 'ignored',
  } as const;

  private readonly currentRoute = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  readonly selectedTab = computed(() => {
    const tree = this.router.parseUrl(this.currentRoute());
    const path = this.currentRoute().split('?')[0];
    let selected = 0;
    let specificity = -1;
    this.tabs().forEach((tab, index) => {
      const params = Object.entries(tab.queryParams ?? {});
      if (tab.route === path && params.every(([key, value]) => tree.queryParams[key] === value) && params.length > specificity) {
        selected = index;
        specificity = params.length;
      }
    });
    return selected;
  });
  navigateToTab(event: Event) {
    const tab = this.tabs()[Number((event.target as HTMLSelectElement).value)];
    if (tab) void this.router.navigate([tab.route], { queryParams: tab.queryParams });
  }

  readonly workspace = computed(() => findWorkspaceGroup(this.currentRoute()));
  readonly tabs = computed(() => {
    const workspace = this.workspace();
    if (!workspace) return [];
    return visibleWorkspaceTabs(workspace, this.authService.currentUser()?.role);
  });
}
